import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Divider,
  IconButton,
  InputAdornment,
  Typography,
  useTheme,
} from "@mui/material";
import {
  ButtonType,
  CustomButton,
  CustomLink,
  CustomTextField,
} from "@BehpardazBarin/frontend_components";
import { customInputProps, customLinkProps } from "../constants/Style";
import { useNavigate } from "react-router";
import { useMutation } from "@tanstack/react-query";
import { dolatLogin, getCaptcha, login } from "../services/Login";
import Captcha from "../components/Common/Captcha";
import { RejectReason } from "../utils/Request.types";
import { snackActions } from "../utils/SnackbarUtils";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import {
  hasCaptchaInfo,
  hasRedirectInfo,
  redirectToExternal,
} from "../utils/LoginUtils";
import { useLoginId } from "../hooks/use-loginId";

const Login: React.FC = () => {
  const theme = useTheme();
  const [showPassword, setShowPassword] = useState(false);
  const [pass, setPass] = useState<string | number>("");
  const [userName, setUserName] = useState<string | number>("");
  const [userCaptchaInput, setUserCaptchaInput] = useState<string | number>("");
  const [captchaId, setCaptchaId] = useState<string>("");
  const [captchaImage, setCaptchaImage] = useState<string>("");
  const [requiresCaptcha, setRequiresCaptcha] = useState<boolean>(false);
  const { loginId } = useLoginId();

  const handleChangePass = (val: string | number) => {
    setPass(val);
  };
  const handleChangeUser = (val: string | number) => {
    setUserName(val);
  };

  const loginMutation = useMutation({
    mutationFn: login,
  });
  const getCaptchaMutation = useMutation({
    mutationFn: getCaptcha,
  });

  const navigate = useNavigate();

  const handleLogin = useCallback(() => {
    //اگر در آدرس بار، پارامتر لاگین ایدی وجود نداشت
    if (!loginId) {
      snackActions.error("امکان لاگین وجود ندارد");
      return;
    }
    const params = {
      Username: userName,
      Password: pass,
      loginId: loginId,
      CaptchaId: captchaId,
      Captcha: userCaptchaInput,
    };
    loginMutation.mutate(params, {
      onSuccess: (response) => {
        if (response && response.data) {
          if (response.data.hasOwnProperty("sendSms")) {
            //لاگین دومرحله ای با پیامک
            if (response.data.sendSms === true) {
              navigate(`/otp`, {
                state: { mobile: response.data.mobile },
              });
            }
            //لاگین دومرحله ای با نرم افزار
            else if (response.data.sendSms === false) {
              navigate(`/otp`, { state: { mobile: false } });
            }
          } else if (hasRedirectInfo(response.data)) {
            redirectToExternal(response.data.url);
          }
        }
        // Optional: Clear caches or handle successful login
      },
      onError: (error: RejectReason) => {
        if (error && error.data) {
          if (hasCaptchaInfo(error.data)) {
            // we have captcha info now
            const data = error.data;
            setCaptchaId(data.captchaId);
            setCaptchaImage(data.captchaImage);
            setRequiresCaptcha(data.requiresCaptcha);
          } else if (hasRedirectInfo(error.data)) {
            // we have redirect info now
            redirectToExternal(error.data.url);
          }
        }
      },
    });
  }, [
    userName,
    pass,
    userCaptchaInput,
    captchaId,
    loginId,
    navigate,
    loginMutation,
  ]);

  const handleDolatLogin = () => {
    if (!loginId) {
      snackActions.error("امکان لاگین وجود ندارد");
      return;
    }
    dolatLogin({ loginId })
      .then((response) => {
        if (response && typeof response === "string") {
          redirectToExternal(response);
        } else {
          snackActions.error("خطا در ورود با دولت من رخ داده است");
          return;
        }
      })
      .catch((error) => {
        if (error && error.data) {
          snackActions.error(error.message);
          redirectToExternal(error.data.url);
        }
      });
  };

  const onResetCaptcha = () => {
    const params = { loginId: loginId };
    getCaptchaMutation.mutate(params, {
      onSuccess: (response) => {
        if (response && response.data) {
          if (hasCaptchaInfo(response.data)) {
            // we have captcha info now
            const data = response.data;
            setCaptchaId(data.captchaId);
            setCaptchaImage(data.captchaImage);
            setRequiresCaptcha(data.requiresCaptcha);
          }
        }
      },
    });
  };

  useEffect(() => {
    const handleEnter = (e: KeyboardEvent) => {
      if (e.key === "Enter") {
        e.preventDefault();
        handleLogin();
      }
    };
    document.addEventListener("keydown", handleEnter);
    return () => {
      document.removeEventListener("keydown", handleEnter);
    };
  }, [handleLogin]);

  return (
    <>
      <Box mb={5}>
        <Typography
          variant="h5"
          gutterBottom
          textAlign="center"
          sx={{ fontFamily: "IRANSansXBold" }}
        >
          خوش آمدید
        </Typography>
        <Typography
          variant="body2"
          textAlign="center"
          sx={{ color: "#1212128F", fontFamily: "IRANSansX" }}
        >
          برای ورود، نام کاربری و رمز عبور خود را وارد کنید.
        </Typography>
      </Box>
      <CustomTextField
        label="نام کاربری"
        value={userName}
        placeholder={"نام کاربری خود را وارد کنید..."}
        onChange={handleChangeUser}
        {...customInputProps(theme)}
        autoFocus={true}
        name="username"
        autoComplete="username"
      />

      <CustomTextField
        label={"رمز عبور"}
        placeholder={"رمز عبور خود را وارد کنید..."}
        onChange={handleChangePass}
        value={pass}
        {...customInputProps(theme)}
        type={showPassword ? "text" : "password"}
        name="password"
        autoComplete="current-password"
        autoFocus={false}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                aria-label="toggle password visibility"
                onClick={() => setShowPassword((prev) => !prev)}
                edge="end"
              >
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          mb: "20px",
          mr: "25px",
        }}
      >
        <CustomLink
          title="فراموشی رمز عبور"
          href="/forgotpassword"
          {...customLinkProps(theme)}
        />
      </Box>

      {requiresCaptcha && captchaImage !== "" && (
        <Captcha
          captchaImage={captchaImage}
          value={userCaptchaInput}
          onInputChange={setUserCaptchaInput}
          onResetCaptcha={onResetCaptcha}
        />
      )}

      <CustomButton
        value={"ورود"}
        onClickHandler={handleLogin}
        buttonType={ButtonType.actionButton}
        customButtonStyle={{
          backgroundColor: "#00695f",
          borderRadius: "12px",
          width: "97%",
          marginTop: 4,
        }}
        textStyle={{
          fontFamily: "IRANSansX",
        }}
      />
      <Divider>
        <Typography sx={{ fontFamily: "IRANSansX", color: "#1212128F" }}>
          یا
        </Typography>
      </Divider>
      <CustomButton
        value={"ورود از طریق دولت من"}
        onClickHandler={handleDolatLogin}
        buttonType={ButtonType.actionButton}
        customButtonStyle={{
          backgroundColor: "#00695f",
          borderRadius: "12px",
          width: "97%",
          marginTop: 2,
        }}
        textStyle={{
          fontFamily: "IRANSansX",
        }}
      />
      <Typography
        variant="body2"
        mt={5}
        textAlign="center"
        color="#1212128F"
        sx={{ fontFamily: "IRANSansX" }}
      >
        <Box component="span" sx={{ marginRight: 0.5 }}>
          حساب کاربری ندارید؟
        </Box>
        <CustomLink
          title="ثبت نام کنید"
          href="/register"
          {...customLinkProps(theme)}
        />
      </Typography>
    </>
  );
};

export default Login;
